export const UNSAFE_PROTOCOLS = [
  "blob:",
  "data:",
  "file:",
  "javascript:",
  "vbscript:",
];

const OPAQUE_ORIGIN = "null";

/**
 * The base that a relative href resolves against.
 *
 * A game inside a sandboxed iframe in a client app has the opaque origin
 * "null", which is not a usable base. The document base URL survives that,
 * so it stands in. Without it a protocol-relative href such as
 * `//sponsor.example.com/offer` would not parse at all.
 */
function getPageBase(): string {
  const { origin } = window.location;

  if (origin && origin !== OPAQUE_ORIGIN) {
    return origin;
  }

  return document.baseURI;
}

/**
 * The origin that an href is compared against. It tracks `getPageBase`, so a
 * relative href under an opaque origin resolves and compares against the same
 * document.
 */
function getPageOrigin(): string {
  const { origin } = window.location;

  if (origin && origin !== OPAQUE_ORIGIN) {
    return origin;
  }

  try {
    return new URL(document.baseURI).origin;
  } catch {
    return origin;
  }
}

/**
 * Parses a URL string against the page base, so that relative URLs (e.g.
 * `/path`, `./path`, `#hash`) resolve instead of throwing. Returns `null` for
 * completely unparseable inputs.
 *
 * The second attempt covers a base that is itself unusable. An absolute href
 * still parses on its own.
 */
export function tryParseURL(href: string): URL | null {
  try {
    return new URL(href, getPageBase());
  } catch {
    // Fall through to the base-less attempt.
  }

  try {
    return new URL(href);
  } catch {
    return null;
  }
}

export function hasSafeScheme(href: string) {
  const url = tryParseURL(href);
  if (!url) {
    return false;
  }

  return !UNSAFE_PROTOCOLS.includes(url.protocol);
}

export function hasHTTPScheme(href: string) {
  const url = tryParseURL(href);
  if (!url) {
    return false;
  }

  return url.protocol === "http:" || url.protocol === "https:";
}

/**
 * Returns true when the href resolves to an origin other than the current
 * one. A custom-scheme deep link, such as `yc-nfl-tb://feature/offer`, has
 * the origin "null", so it counts as a different origin.
 */
export function hasDifferentOrigin(href: string) {
  const url = tryParseURL(href);
  if (!url) {
    return false;
  }

  // A scheme the URL standard does not treat as special, such as a deep link,
  // always has the opaque origin "null". The page can carry the same opaque
  // origin inside a sandboxed iframe, and comparing the two would call the
  // link internal. A relative href does not reach this branch, because it
  // resolves against the document base.
  if (url.origin === OPAQUE_ORIGIN) {
    return true;
  }

  return url.origin !== getPageOrigin();
}
